import React, { FC, useCallback, useEffect, useMemo } from 'react';

import { PageContext } from '../../modules/context/PageContext';

import { useProfileStore } from '../../modules/store/profileStore';
import { useSavedCardsStore } from '../../modules/store/savedCardsStore';
import { PROFILE_CONSTANTS, getTrackingWithCustomClickValue, buildWalletBadgeText } from '../../modules';
import { useWalletWidgetBadgePublisher } from '../WalletWidgetContainer';
import {
  WALLET_SAVED_CARDS_TEST_IDS,
  WALLET_SAVED_CARDS_TRACKING_KEYS,
  SAVED_CARDS_MODAL_IDS,
} from './WalletSavedCardsSection.constants';
import { useWalletSavedCards } from './hooks/useWalletSavedCards';
import { SavedCardRow } from './molecules/SavedCardRow';
import { AddCardRow } from './molecules/AddCardRow';
import { StyledWalletSavedCardsSection } from './WalletSavedCardsSection.styles';
import { WalletSavedCardsSectionProps, SavedCard } from './WalletSavedCardsSection.types';
import { walletSavedCardsSectionMock } from './__mock__/WalletSavedCardsSectionMock';

/**
 * WalletSavedCardsSection - Orchestrator component for saved cards in Wallet Tab
 * Reuses Gift Cards section styling and structure patterns
 */
export const WalletSavedCardsSection: FC<WalletSavedCardsSectionProps> = ({
  isAuthorMode,
  model,
  pageContext: pageContextProp,
}) => {
  const { NEXT_PUBLIC_IS_LOCAL_DEV } = process.env;
  const reactPageContext = React.useContext(PageContext);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pageContext = (pageContextProp ?? reactPageContext) as Record<string, any>;

  const isLocalDev = NEXT_PUBLIC_IS_LOCAL_DEV === 'true';
  const useMockData = Boolean(isAuthorMode) || isLocalDev;

  // Store access
  const { setProfileModalId } = useProfileStore(state => state);
  const { savedCards } = useSavedCardsStore(state => ({
    savedCards: state.savedCards,
  }));

  // Hide guard
  const isHidden = Boolean(model?.hideComponent) && !useMockData;

  // Seed mock data for author mode and local dev
  useEffect(() => {
    if (useMockData && !isHidden) {
      useSavedCardsStore.getState().setProfileSavedCards(walletSavedCardsSectionMock);
    }
  }, [useMockData, isHidden]);

  // Fetch saved cards from API
  useWalletSavedCards({
    pageContext,
    isLocal: NEXT_PUBLIC_IS_LOCAL_DEV === 'true',
    skip: useMockData,
  });

  // Use mock data if in mock mode, otherwise use real data
  const cardsToDisplay = useMemo(() => {
    if (useMockData) {
      return walletSavedCardsSectionMock;
    }
    // In production, use data from store (populated by API via useWalletSavedCards)
    return savedCards || [];
  }, [useMockData, savedCards]);

  // Loading state
  const isLoading = useMemo(() => {
    // Always false for now - we're always showing mock data
    return false;
  }, []);

  // Sort cards: Primary first, then by order
  const sortedCards = useMemo(() => {
    if (!cardsToDisplay || cardsToDisplay.length === 0) return [];
    const sorted = [...cardsToDisplay];
    sorted.sort((a, b) => {
      if (a.isPrimary) return -1;
      if (b.isPrimary) return 1;
      return 0;
    });
    return sorted;
  }, [cardsToDisplay]);

  const cardCount = sortedCards.length;
  const hasCards = !isLoading && cardCount > 0;

  // Badge text (singular/plural handling)
  const badgeText = useMemo(() => {
    if (isHidden) return undefined;
    if (cardCount === 0) {
      return model?.defaultLabel;
    }
    // Use singular label for count === 1, plural otherwise
    const label = cardCount === 1 ? model?.availableSingularLabel : model?.availablePluralLabel;
    if (label?.includes('{0}')) {
      return label.replace('{0}', String(cardCount));
    }
    return label ? `${cardCount} ${label}` : undefined;
  }, [cardCount, isHidden, model?.defaultLabel, model?.availableSingularLabel, model?.availablePluralLabel]);

  useWalletWidgetBadgePublisher(badgeText);

  // Extract labels from nested structure
  const addCardLabels = {
    title: model?.addCard?.sectionHeading,
    subtitle: model?.addCard?.description,
    ctaLabel: model?.addCard?.ctaLinkLabel,
  };

  const cardListLabels = {
    primaryLabel: model?.cardList?.primaryLabel,
    editCtaLabel: model?.cardList?.editLinkLabel,
  };

  // Tracking
  const { trackingProperties: _addCtaTracking, customClickTrackValue: _addCtaCustomTrack } = useMemo(
    () =>
      getTrackingWithCustomClickValue(
        model?.addCard?.tracking ? [model.addCard.tracking] : undefined,
        WALLET_SAVED_CARDS_TRACKING_KEYS.addCta
      ),
    [model?.addCard?.tracking]
  );

  // Modal handlers
  const handleAddCardClick = useCallback(() => {
    setProfileModalId(SAVED_CARDS_MODAL_IDS.ADD_CARD);
  }, [setProfileModalId]);

  const handleEditCardClick = useCallback(
    (card: SavedCard) => {
      useSavedCardsStore.getState().setSelectedSavedCard(card);
      setProfileModalId(SAVED_CARDS_MODAL_IDS.EDIT_CARD);
    },
    [setProfileModalId]
  );

  const _handleDeleteCardClick = useCallback(
    (card: SavedCard) => {
      useSavedCardsStore.getState().setSelectedSavedCard(card);
      setProfileModalId(SAVED_CARDS_MODAL_IDS.DELETE_CARD);
    },
    [setProfileModalId]
  );

  const _handleFolioDeliveryClick = useCallback(
    (card: SavedCard) => {
      useSavedCardsStore.getState().setSelectedSavedCard(card);
      setProfileModalId(SAVED_CARDS_MODAL_IDS.FOLIO_DELIVERY);
    },
    [setProfileModalId]
  );

  // Early return after all hooks
  if (isHidden) {
    return null;
  }

  return (
    <StyledWalletSavedCardsSection
      data-testid={WALLET_SAVED_CARDS_TEST_IDS.root}
      data-component-name="o-account-WalletSavedCardsSection"
      id={model?.componentId}
      className={model?.customClass}
    >
      {/* Cards list */}
      {hasCards && (
        <div className="wallet-saved-cards-section__cards-list" data-testid={WALLET_SAVED_CARDS_TEST_IDS.cardsList}>
          {sortedCards.map(card => (
            <SavedCardRow
              key={card.id}
              card={card}
              primaryLabel={cardListLabels.primaryLabel}
              editCtaLabel={cardListLabels.editCtaLabel}
              editCtaAriaLabel={cardListLabels.editCtaLabel}
              onEditClick={handleEditCardClick}
              testId={WALLET_SAVED_CARDS_TEST_IDS.cardRow}
            />
          ))}
        </div>
      )}

      {/* Add Card row - always render */}
      <AddCardRow
        title={addCardLabels.title}
        subtitle={addCardLabels.subtitle}
        ctaLabel={addCardLabels.ctaLabel}
        ctaAriaLabel={addCardLabels.ctaLabel}
        onAddClick={handleAddCardClick}
        testId={WALLET_SAVED_CARDS_TEST_IDS.addCardRow}
        showDivider={hasCards}
      />
    </StyledWalletSavedCardsSection>
  );
};
